/**
 * DSCR Calculations — the UW workflow between the sizer's stated inputs and the
 * lock snapshot. Sibling of the Liquidity Calculations tab and deliberately its
 * twin: every value is a row with document evidence, an UW haircut
 * (`countedPercentage`) and a verified flag, and the headline number exists at
 * three confidence levels rather than one.
 *
 * Backend: `GET /pricing/dscr_calc/{dealId}` · `POST` the same path to save ·
 * `GET /pricing/dscr_calc_components` (`DscrCalc` / `DscrValueSource` in
 * pricing_structures.py — on the PRICING router because the calc feeds
 * `DSCRLock.calculated_dscr_at_lock`).
 *
 * EVERY FIGURE IS BACKEND-OWNED — `statedDscr`, `validatedDscr`, `verifiedDscr`,
 * `monthlyPiPayment`, `pitiaValidated`, `componentSummary`, `clearsFloor`,
 * `fullyVerified`, and per row `monthlyAmount` / `validatedMonthlyAmount` /
 * `isIncome`. This tab renders them and only sums the per-table footers, so it
 * cannot disagree with the quick price or the lock.
 *
 * A first visit is already populated: with nothing stored, the GET SEEDS rows
 * from the units and subject property, so UW reviews and verifies rather than
 * keying blanks.
 *
 * DSCR-product only — mounted in the drilldown behind a `loanProductType`
 * check, the same way Budget & Draws is gated to construction products.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import AddOutlined from "@mui/icons-material/AddOutlined";
import CalculateOutlined from "@mui/icons-material/CalculateOutlined";
import HistoryOutlined from "@mui/icons-material/HistoryOutlined";
import SyncOutlined from "@mui/icons-material/SyncOutlined";
import isNil from "lodash/isNil";
import size from "lodash/size";
import { useSnackbar } from "notistack";

import PrimaryContentCard from "../../../../_src_shared/components/layout/PrimaryContentCard";
import EmptyStatePlaceholder from "../../../../components/feedback/EmptyStatePlaceholder";
import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { dscrCalcApi } from "../../../../api/dscrCalcApi";
import { loanDrilldownSidebarTypes } from "../../../../constants/enums/loanDrilldownScreenSidebarOptions";
import {
  sidebarSetStateValue,
  sidebarTypeSet,
} from "../../../../redux/actions/sidebarActions";
import {
  loanFetchedDataDscrCalcComponentsSet,
  loanFetchedDataDscrCalcEvidenceSet,
  loanFetchedDataDscrCalcSet,
} from "../../../../redux/reducers/loanFetchedDataReducer";
import formatDollar from "../../../../_src_shared/utils/functions/formatDollar";
import { dscrPaymentBasisLabel } from "../../../../utils/loan/dscrPaymentBasis";
import { slate } from "../../../../theme/colors";
import LiquidityCardSectionHeader from "../LoanDrilldownLiquidityCalculationsTab/LiquidityCardSectionHeader";
import DscrChangeHistoryDrawer from "./DscrChangeHistoryDrawer";
import DscrChangeHistoryList from "./DscrChangeHistoryList";
import DscrSourcesTable from "./DscrSourcesTable";
import DscrSummaryColumn from "./DscrSummaryColumn";
import DscrSupportingFiles from "./DscrSupportingFiles";
import { isIncomeRow } from "./constants";

const fmtDscr = (v) =>
  v == null || Number.isNaN(Number(v)) ? "—" : `${Number(v).toFixed(2)}×`;

// Document groups / types the workbook draws its evidence from. `Tax Certs`
// lives under Title Docs and `Property Tax Record` under Property Documents
// in the taxonomy — there is no tax group — so those are matched by type.
const SUPPORTING_DOC_GROUPS = ["Leases & Rents", "Appraisal", "Insurance Docs"];
const SUPPORTING_DOC_TYPES = ["Tax Certs", "Property Tax Record"];

/* ─────────────────────────── per-table footers ──────────────────────────── */

// Footer sums ONLY, over the SERVER's per-row computed fields (`monthlyAmount`
// already has Annual* ÷ 12 applied, `validatedMonthlyAmount` the haircut too).
// Nothing here derives a DSCR, a PITIA or a floor check — those are computed
// fields on DscrCalc, so the tab can't disagree with the lock.
const sumRows = (rows) =>
  (rows || []).reduce(
    (acc, s) => ({
      gross: acc.gross + (Number(s?.monthlyAmount) || 0),
      validated: acc.validated + (Number(s?.validatedMonthlyAmount) || 0),
    }),
    { gross: 0, validated: 0 },
  );

const SourcesSection = ({
  title,
  subTitle,
  sources,
  loan,
  components,
  onAdd,
  onRowClick,
  activeSourceId,
  totalGross,
  totalValidated,
}) => (
  <Box>
    <LiquidityCardSectionHeader
      title={`${title} (${size(sources)})`}
      subTitle={subTitle}
      endContent={
        <Button
          size="small"
          startIcon={<AddOutlined sx={{ fontSize: 14 }} />}
          onClick={onAdd}
        >
          Add source
        </Button>
      }
    />
    {size(sources) === 0 ? (
      <EmptyStatePlaceholder
        icon={<CalculateOutlined />}
        title={`No ${title.toLowerCase()} yet`}
        description="Add a row, attach the document that evidences it, and mark it verified once UW has reviewed."
      />
    ) : (
      <>
        <DscrSourcesTable
          sources={sources}
          loan={loan}
          components={components}
          onRowClick={onRowClick}
          activeSourceId={activeSourceId}
        />
        <Flx ac jb g={2} sx={{ px: 0.5, pt: 1.25 }}>
          <Txt sx={{ fontSize: 12, color: slate[500] }}>
            Monthly total ({size(sources)} row{size(sources) === 1 ? "" : "s"}) ·{" "}
            {sources.filter((s) => s?.verified).length} verified
          </Txt>
          <Flx ac g={1}>
            <Txt sx={{ fontSize: 12.5, color: slate[500] }}>
              stated {formatDollar(totalGross, "—")}
            </Txt>
            <Txt sx={{ fontSize: 12.5, color: slate[400] }}>·</Txt>
            <Txt sx={{ fontSize: 13, fontWeight: 700 }}>
              validated {formatDollar(totalValidated, "—")}
            </Txt>
          </Flx>
        </Flx>
      </>
    )}
  </Box>
);

/* ─────────────────────────────────── tab ─────────────────────────────────── */

const LoanDrilldownDscrCalculationsTab = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const loan = useSelector((state) => state.loanDrilldown);
  const calc = useSelector((state) => state.loanFetchedData?.dscrCalc);
  // The catalog lives in redux (not local state) so the Add-Source sidebar
  // reads the same copy and can filter it by section.
  const components = useSelector(
    (state) => state.loanFetchedData?.dscrCalcComponents,
  );
  const [loading, setLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Active row = the one open in the sidebar, so the table can highlight it.
  const sidebarType = useSelector((state) => state.sidebar?.type);
  const sidebarState = useSelector((state) => state.sidebar?.state);
  const activeSourceId =
    sidebarType === loanDrilldownSidebarTypes.ADD_EDIT_DSCR_INCOME_SOURCE ||
    sidebarType === loanDrilldownSidebarTypes.ADD_EDIT_DSCR_EXPENSE_SOURCE
      ? sidebarState?._id || null
      : null;

  useEffect(() => {
    if (!isNil(calc) || !loan?._id) return;
    setLoading(true);
    dscrCalcApi
      .get(loan._id)
      .then((data) => dispatch(loanFetchedDataDscrCalcSet(data || {})))
      .catch((err) => {
        dispatch(loanFetchedDataDscrCalcSet({}));
        enqueueSnackbar(
          err?.response?.data?.detail || "Couldn't load the DSCR calculation",
          { variant: "error" },
        );
      })
      .finally(() => setLoading(false));
  }, [calc, loan?._id, dispatch, enqueueSnackbar]);

  // The component enum drives the Add drawer and the income/expense split. A
  // failure is non-fatal — constants.js carries the same list as a fallback.
  useEffect(() => {
    if (components) return;
    dscrCalcApi
      .components()
      .then((data) =>
        dispatch(
          loanFetchedDataDscrCalcComponentsSet(
            Array.isArray(data) ? data : data?.components || [],
          ),
        ),
      )
      .catch(() => dispatch(loanFetchedDataDscrCalcComponentsSet([])));
  }, [components, dispatch]);

  const sources = useMemo(() => calc?.sources || [], [calc]);
  const incomeSources = useMemo(
    () => sources.filter((s) => isIncomeRow(s, components)),
    [sources, components],
  );
  const expenseSources = useMemo(
    () => sources.filter((s) => !isIncomeRow(s, components)),
    [sources, components],
  );
  const incomeTotals = useMemo(() => sumRows(incomeSources), [incomeSources]);
  const expenseTotals = useMemo(() => sumRows(expenseSources), [expenseSources]);
  const verifiedCount = useMemo(
    () => sources.filter((s) => s?.verified).length,
    [sources],
  );

  // The floor is stored on the calc, not defaulted server-side, so setting it
  // is a save of the same document — sources unchanged, floor replaced.
  const [savingFloor, setSavingFloor] = useState(false);
  const saveFloor = useCallback(
    (programFloor) => {
      if (!loan?._id) return;
      setSavingFloor(true);
      dscrCalcApi
        .save(loan._id, { sources, programFloor })
        .then((data) => dispatch(loanFetchedDataDscrCalcSet(data)))
        .catch((err) =>
          enqueueSnackbar(
            err?.response?.data?.detail || "Couldn't save the program floor",
            { variant: "error" },
          ),
        )
        .finally(() => setSavingFloor(false));
    },
    [loan?._id, sources, dispatch, enqueueSnackbar],
  );

  // Two drawers, not one (owner 2026-09-15): income rows open the unit-tied
  // income drawer, expense rows the property-tied expense drawer. Add is
  // launched from a section header, so the bucket is known; edit derives it
  // from the row.
  const openSidebar = useCallback(
    (source, bucket) => {
      const target =
        bucket || (isIncomeRow(source, components) ? "income" : "expense");
      dispatch(
        sidebarTypeSet(
          target === "income"
            ? loanDrilldownSidebarTypes.ADD_EDIT_DSCR_INCOME_SOURCE
            : loanDrilldownSidebarTypes.ADD_EDIT_DSCR_EXPENSE_SOURCE,
        ),
      );
      dispatch(sidebarSetStateValue(source || null));
    },
    [dispatch, components],
  );
  // Refresh drops the calc AND the evidence cache — a new extract should show.
  const refresh = useCallback(() => {
    dispatch(loanFetchedDataDscrCalcSet(null));
    dispatch(loanFetchedDataDscrCalcEvidenceSet(null));
  }, [dispatch]);
  const historyCount = size(calc?.changeHistory);

  // Supporting files: the groups the workbook draws on, plus the two tax doc
  // TYPES (there is no tax group in the taxonomy — `Tax Certs` sits under
  // Title Docs, `Property Tax Record` under Property Documents), plus anything
  // the evidence projection parsed. Evidence is fetched lazily for the cards.
  const evidence = useSelector((state) => state.loanFetchedData?.dscrCalcEvidence);
  useEffect(() => {
    if (evidence != null || !loan?._id) return;
    dscrCalcApi
      .evidence(loan._id)
      .then((rows) => dispatch(loanFetchedDataDscrCalcEvidenceSet(rows || [])))
      .catch(() => dispatch(loanFetchedDataDscrCalcEvidenceSet([])));
  }, [evidence, loan?._id, dispatch]);
  const evidenceByDocId = useMemo(() => {
    const m = new Map();
    (Array.isArray(evidence) ? evidence : []).forEach((e) => {
      if (e?.loanDocumentId) m.set(String(e.loanDocumentId), e);
    });
    return m;
  }, [evidence]);
  const dscrFiles = useMemo(
    () =>
      (loan?.loanDocuments || []).filter(
        (d) =>
          SUPPORTING_DOC_GROUPS.includes(d?.docGroup) ||
          SUPPORTING_DOC_TYPES.includes(d?.docType) ||
          evidenceByDocId.has(String(d?._id)),
      ),
    [loan?.loanDocuments, evidenceByDocId],
  );

  const isInitialLoading = isNil(calc) && loading;

  return (
    <Box sx={{ py: 4, px: 3 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(0, 2fr) minmax(0, 1fr)",
          },
          gap: 3,
          maxWidth: "1600px",
          mx: "auto",
        }}
      >
        <Flx column gap={3} sx={{ minWidth: 0 }}>
          <PrimaryContentCard
            loading={isInitialLoading}
            title="DSCR calculations"
            subTitle="Track the income and expense sources behind this loan's DSCR. Each row carries its evidence, its UW haircut, and whether it has been verified. Rent rows belong to units; expense rows to the property."
            // The header row wraps, so a long subtitle pushes `endContent` under
            // the title. The toolbar is anchored to the card's top-right corner
            // instead, and the header keeps clear of it.
            cardSx={{ position: "relative" }}
            headerSx={{ pr: 24 }}
          >
            <Flx ac g={0.5} sx={{ position: "absolute", top: 26, right: 28 }}>
              <Tooltip title="Change history" placement="top">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setHistoryOpen(true)}
                    disabled={isNil(calc)}
                  >
                    <Badge
                      badgeContent={historyCount || 0}
                      color="primary"
                      max={99}
                      sx={{ "& .MuiBadge-badge": { fontSize: 10, height: 16, minWidth: 16 } }}
                    >
                      <HistoryOutlined />
                    </Badge>
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Refresh DSCR data" placement="top">
                <span>
                  <IconButton size="small" onClick={refresh} disabled={isNil(calc)}>
                    <SyncOutlined />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Open the work-in-progress redesign of this tab" placement="top">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AutoAwesomeOutlined sx={{ fontSize: 15 }} />}
                  onClick={() => navigate(`/loan/${loan?._id}/dscr-workbook`)}
                  sx={{ ml: 0.5 }}
                >
                  Redesign (WIP)
                </Button>
              </Tooltip>
            </Flx>
            <Divider sx={{ mt: 0.5, mb: 5, borderWidth: "1.5px" }} />
            <Flx column g={0} sx={{ px: 1 }}>
              {size(sources) === 0 ? (
                <EmptyStatePlaceholder
                  icon={<CalculateOutlined />}
                  title="No DSCR sources yet"
                  description="Add the rent, tax, insurance, HOA and other rows behind this loan's DSCR — each with the document that evidences it."
                  actions={
                    // Two entry points, not one — the drawer is always
                    // scoped to a section, so the empty state offers both.
                    <Flx ac g={1} wrap jc>
                      <Button
                        size="medium"
                        startIcon={<AddCircleOutline />}
                        onClick={() => openSidebar(null, "income")}
                      >
                        Add income source
                      </Button>
                      <Button
                        size="medium"
                        variant="outlined"
                        startIcon={<AddCircleOutline />}
                        onClick={() => openSidebar(null, "expense")}
                      >
                        Add expense source
                      </Button>
                    </Flx>
                  }
                />
              ) : (
                <>
                  <SourcesSection
                    title="Income sources"
                    subTitle="Rows add within a component — one per unit / lease. The workbook counts each row as entered (after its counted %); guideline caps such as STR at 125% of market are applied through the amount or the haircut, not automatically."
                    sources={incomeSources}
                    loan={loan}
                    components={components}
                    onAdd={() => openSidebar(null, "income")}
                    onRowClick={openSidebar}
                    activeSourceId={activeSourceId}
                    totalGross={incomeTotals.gross}
                    totalValidated={incomeTotals.validated}
                  />
                  <Divider sx={{ my: 6, mx: -2 }} />
                  <SourcesSection
                    title="Expense sources"
                    subTitle="Annual components are entered as printed and normalised to a monthly figure server-side before they join PITIA."
                    sources={expenseSources}
                    loan={loan}
                    components={components}
                    onAdd={() => openSidebar(null, "expense")}
                    onRowClick={openSidebar}
                    activeSourceId={activeSourceId}
                    totalGross={expenseTotals.gross}
                    totalValidated={expenseTotals.validated}
                  />
                </>
              )}

              <Divider sx={{ my: 6, mx: -2 }} />

              <Box>
                <LiquidityCardSectionHeader
                  title="Payment (denominator)"
                  subTitle="P&I comes from the loan's terms; PITIA adds the validated expense rows above. Both are server fields."
                />
                <Flx g={8} wrap sx={{ px: 0.5, py: 1 }}>
                  <Flx column g={0.25}>
                    <Txt sx={{ fontSize: 11.5, color: slate[500] }}>Monthly P&amp;I</Txt>
                    <Txt sx={{ fontSize: 20, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                      {formatDollar(calc?.monthlyPiPayment, "—")}
                    </Txt>
                    {/* The auto-pulled inputs behind the PMT — `amortizing`,
                        `loanAmount`, `interestRate` (a fraction) on the calc,
                        plus the loan's amortization type. */}
                    <Txt sx={{ fontSize: 11, color: slate[500] }}>
                      {dscrPaymentBasisLabel(calc, loan?.amortizationType)}
                    </Txt>
                  </Flx>
                  <Flx column g={0.25}>
                    <Txt sx={{ fontSize: 11.5, color: slate[500] }}>PITIA (validated)</Txt>
                    <Txt sx={{ fontSize: 20, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                      {formatDollar(calc?.pitiaValidated, "—")}
                    </Txt>
                    <Txt sx={{ fontSize: 11, color: slate[500] }}>expenses + P&amp;I</Txt>
                  </Flx>
                </Flx>
                {/* The equation with the server's numbers in it — income and
                    expenses are sums of per-row `validatedMonthlyAmount`, the
                    ratio is `validatedDscr`. Shown, never computed here. */}
                <Flx
                  ac
                  g={0.75}
                  wrap
                  sx={{
                    mt: 1.5,
                    px: 2,
                    py: 1.25,
                    borderRadius: "8px",
                    bgcolor: slate[50],
                    border: `1px solid ${slate[200]}`,
                    fontSize: 12.5,
                  }}
                >
                  <Txt sx={{ fontWeight: 700 }}>{formatDollar(incomeTotals.validated, "—")}</Txt>
                  <Txt sx={{ color: slate[600] }}>income</Txt>
                  <Txt sx={{ color: slate[400] }}>÷</Txt>
                  <Txt sx={{ color: slate[400] }}>(</Txt>
                  <Txt sx={{ fontWeight: 700 }}>{formatDollar(expenseTotals.validated, "—")}</Txt>
                  <Txt sx={{ color: slate[600] }}>expenses</Txt>
                  <Txt sx={{ color: slate[400] }}>+</Txt>
                  <Txt sx={{ fontWeight: 700 }}>{formatDollar(calc?.monthlyPiPayment, "—")}</Txt>
                  <Txt sx={{ color: slate[600] }}>P&amp;I</Txt>
                  <Txt sx={{ color: slate[400] }}>)</Txt>
                  <Txt sx={{ color: slate[400] }}>=</Txt>
                  <Txt sx={{ fontWeight: 700 }}>{fmtDscr(calc?.validatedDscr)}</Txt>
                  <Txt sx={{ color: slate[600] }}>validated DSCR</Txt>
                </Flx>
              </Box>

              <Divider sx={{ my: 6, mx: -2 }} />

              <Box>
                <LiquidityCardSectionHeader
                  title={`Supporting files (${size(dscrFiles)})`}
                  subTitle="Loan documents in the Leases & Rents, Appraisal and Insurance groups, plus tax certificates and records. Parsed documents show the values they can supply — display only; “Use this value” lives inside a row's drawer and is always your act."
                />
                {size(dscrFiles) === 0 ? (
                  <EmptyStatePlaceholder
                    icon={<CalculateOutlined />}
                    title="No supporting files yet"
                    description="Upload leases, the appraisal, insurance or tax documents from the Documents tab to evidence these rows."
                  />
                ) : (
                  <DscrSupportingFiles docs={dscrFiles} evidenceByDocId={evidenceByDocId} />
                )}
              </Box>

              <Divider sx={{ my: 6, mx: -2 }} />

              <Box>
                <LiquidityCardSectionHeader
                  title={`Change history (${historyCount})`}
                  subTitle="Who changed what, old → new. A verification cleared by the server is labeled as its reset; empty saves never appear. The initial entry stays pinned as the baseline."
                />
                <DscrChangeHistoryList
                  history={calc?.changeHistory}
                  initialEntry={calc?.initialEntry}
                  loan={loan}
                />
              </Box>
            </Flx>
          </PrimaryContentCard>
        </Flx>

        <Box
          sx={{
            position: { xs: "static", md: "sticky" },
            top: 24,
            alignSelf: "flex-start",
            minWidth: 0,
          }}
        >
          <DscrSummaryColumn
            calc={calc}
            sourceCount={sources.length}
            verifiedCount={verifiedCount}
            incomeValidated={incomeTotals.validated}
            expenseValidated={expenseTotals.validated}
            onSaveFloor={saveFloor}
            savingFloor={savingFloor}
          />
        </Box>
      </Box>

      <DscrChangeHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        history={calc?.changeHistory}
        initialEntry={calc?.initialEntry}
        loan={loan}
      />
    </Box>
  );
};

export default LoanDrilldownDscrCalculationsTab;
